import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GOOGLE_API_KEY } from '../url';
import './AddressInput.css';

// Load Google Maps Script
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      existingScript.addEventListener('error', reject);
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const AddressInput = ({
  value,
  onChange,
  onBlur,
  placeholder = "Enter your address",
  name = "address",
  error,
  touched,
  disabled = false,
  icon = null,
  showIcon = true,
  onLocationChange = null  // Callback for lat/lng
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [googleLoaded, setGoogleLoaded] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  // Load Google Maps Script on mount
  useEffect(() => {
    loadGoogleMapsScript()
      .then((google) => {
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
        // Create a dummy div for PlacesService (required by Google)
        const dummyDiv = document.createElement('div');
        placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
        // Create session token for billing optimization
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        setGoogleLoaded(true);
      })
      .catch((err) => {
        setLocationError('Failed to load Google Maps. Please refresh.');
      });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Search locations using Google Places Autocomplete
  const searchLocations = useCallback(async (query) => {
    if (!query || query.length < 3 || !googleLoaded || !autocompleteServiceRef.current) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setLocationError('');

    try {
      const request = {
        input: query,
        componentRestrictions: { country: 'in' }, // Restrict to India
        sessionToken: sessionTokenRef.current,
        types: ['geocode', 'establishment'] // Include both addresses and places
      };

      autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
        setIsSearching(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSearchResults([]);
        } else {
          setSearchResults([]);
        }
      });
    } catch (err) {
      setLocationError('Search failed. Please try again.');
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [googleLoaded]);

  // Debounced search - waits 300ms after user stops typing
  const debouncedSearch = useCallback((query) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchLocations(query);
    }, 300);
  }, [searchLocations]);

  // Handle selecting a search result
  const handleSelectLocation = (prediction) => {
    if (!placesServiceRef.current) return;

    setIsSearching(true);

    // Get place details for lat/lng
    const request = {
      placeId: prediction.place_id,
      fields: ['formatted_address', 'geometry', 'address_components'],
      sessionToken: sessionTokenRef.current
    };

    placesServiceRef.current.getDetails(request, (place, status) => {
      setIsSearching(false);

      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const address = place.formatted_address || prediction.description;

        // Update input value
        onChange({ target: { name, value: address } });
        setSearchQuery(address);

        // Extract location data
        if (onLocationChange && place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          // Extract city, state, pincode from address_components
          let city = '';
          let state = '';
          let pincode = '';

          if (place.address_components) {
            place.address_components.forEach((component) => {
              const types = component.types;

              if (types.includes('locality')) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_2') && !city) {
                city = component.long_name;
              } else if (types.includes('administrative_area_level_1')) {
                state = component.long_name;
              } else if (types.includes('postal_code')) {
                pincode = component.long_name;
              }
            });
          }

          onLocationChange({
            latitude: lat,
            longitude: lng,
            city,
            state,
            pincode
          });
        }

        // Create new session token for next search
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

        // Close dropdown and clear results
        setShowDropdown(false);
        setSearchResults([]);
      } else {
        setLocationError('Failed to get location details. Please try again.');
      }
    });
  };

  // Get current location using Geolocation API
  const getCurrentLocation = () => {
    setIsLoading(true);
    setLocationError('');
    setSearchResults([]);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        if (!googleLoaded || !window.google) {
          setLocationError('Google Maps not loaded. Please try again.');
          setIsLoading(false);
          return;
        }

        try {
          // Use Google Geocoder for reverse geocoding
          const geocoder = new window.google.maps.Geocoder();
          const latlng = { lat: latitude, lng: longitude };

          geocoder.geocode({ location: latlng }, (results, status) => {
            setIsLoading(false);

            if (status === 'OK' && results[0]) {
              const place = results[0];
              const address = place.formatted_address;

              onChange({ target: { name, value: address } });
              setSearchQuery(address);

              // Extract location data
              if (onLocationChange) {
                let city = '';
                let state = '';
                let pincode = '';

                place.address_components.forEach((component) => {
                  const types = component.types;

                  if (types.includes('locality')) {
                    city = component.long_name;
                  } else if (types.includes('administrative_area_level_2') && !city) {
                    city = component.long_name;
                  } else if (types.includes('administrative_area_level_1')) {
                    state = component.long_name;
                  } else if (types.includes('postal_code')) {
                    pincode = component.long_name;
                  }
                });

                onLocationChange({
                  latitude,
                  longitude,
                  city,
                  state,
                  pincode
                });
              }
              setShowDropdown(false);
            } else {
              setLocationError('Could not find address for your location');
            }
          });
        } catch (error) {
          setIsLoading(false);
          setLocationError('Failed to get address. Please enter manually.');
        }
      },
      (error) => {
        setIsLoading(false);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enter address manually.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location unavailable. Please enter address manually.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out. Please try again.');
            break;
          default:
            setLocationError('Failed to get location. Please enter address manually.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(e);
    setSearchQuery(newValue);
    setLocationError('');

    // Trigger debounced search
    if (newValue.length >= 3) {
      setShowDropdown(true);
      debouncedSearch(newValue);
    } else {
      setSearchResults([]);
    }
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  // Get icon for place type
  const getPlaceIcon = (types) => {
    if (!types) return 'location';
    if (types.includes('establishment')) return 'building';
    if (types.includes('route')) return 'road';
    return 'location';
  };

  // Get short location type label
  const getLocationType = (prediction) => {
    const types = prediction.types || [];

    if (types.includes('establishment')) return 'Place';
    if (types.includes('street_address')) return 'Address';
    if (types.includes('route')) return 'Road';
    if (types.includes('locality')) return 'City';
    if (types.includes('sublocality')) return 'Area';
    if (types.includes('administrative_area_level_1')) return 'State';
    if (types.includes('postal_code')) return 'Pincode';

    return 'Location';
  };

  return (
    <div className="address-input-container" ref={dropdownRef}>
      <div className={`address-input-wrapper ${error && touched ? 'input-error' : ''} ${!error && touched && value ? 'input-success' : ''}`}>
        {/* Location Icon */}
        {showIcon && (
          icon ? (
            <img src={icon} alt="" className="address-icon" style={{ width: '20px', height: '20px' }} />
          ) : (
            <svg className="address-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17.5 8.33334C17.5 14.1667 10 19.1667 10 19.1667C10 19.1667 2.5 14.1667 2.5 8.33334C2.5 6.34421 3.29018 4.4366 4.6967 3.03007C6.10322 1.62355 8.01088 0.833344 10 0.833344C11.9891 0.833344 13.8968 1.62355 15.3033 3.03007C16.7098 4.4366 17.5 6.34421 17.5 8.33334Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 10.8333C11.3807 10.8333 12.5 9.71406 12.5 8.33334C12.5 6.95263 11.3807 5.83334 10 5.83334C8.61929 5.83334 7.5 6.95263 7.5 8.33334C7.5 9.71406 8.61929 10.8333 10 10.8333Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        )}

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          autoComplete="off"
        />

        {/* Success Icon */}
        {!error && touched && value && !isSearching && (
          <svg className="success-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}

        {/* Loading Spinner */}
        {(isLoading || isSearching) && (
          <div className="address-loading-spinner"></div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && !isLoading && (
        <div className="address-dropdown">
          {/* Current Location Button */}
          <button
            type="button"
            className="current-location-btn"
            onClick={getCurrentLocation}
            disabled={isLoading || !googleLoaded}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 13.3333C11.8409 13.3333 13.3333 11.8409 13.3333 10C13.3333 8.15905 11.8409 6.66667 10 6.66667C8.15905 6.66667 6.66667 8.15905 6.66667 10C6.66667 11.8409 8.15905 13.3333 10 13.3333Z" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 1.66667V4.16667" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 15.8333V18.3333" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1.66667 10H4.16667" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.8333 10H18.3333" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Use Current Location</span>
          </button>

          <div className="dropdown-divider"></div>

          {/* Search Results */}
          {isSearching ? (
            <div className="search-loading">
              <div className="search-loading-spinner"></div>
              <span>Searching...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="search-results">
              <p className="search-results-label">Search Results</p>
              {searchResults.map((prediction, index) => (
                <button
                  key={prediction.place_id || index}
                  type="button"
                  className="search-result-item"
                  onClick={() => handleSelectLocation(prediction)}
                >
                  <div className="search-result-icon">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M14 6.66667C14 11.3333 8 15.3333 8 15.3333C8 15.3333 2 11.3333 2 6.66667C2 5.07537 2.63214 3.54925 3.75736 2.42403C4.88258 1.29881 6.4087 0.666672 8 0.666672C9.5913 0.666672 11.1174 1.29881 12.2426 2.42403C13.3679 3.54925 14 5.07537 14 6.66667Z" stroke="#64748B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 8.66667C9.10457 8.66667 10 7.77124 10 6.66667C10 5.5621 9.10457 4.66667 8 4.66667C6.89543 4.66667 6 5.5621 6 6.66667C6 7.77124 6.89543 8.66667 8 8.66667Z" stroke="#64748B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="search-result-content">
                    <span className="search-result-name">{prediction.description}</span>
                    <span className="search-result-type">{getLocationType(prediction)}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : searchQuery.length >= 3 && !isSearching ? (
            <p className="no-results">No locations found. Try a different search.</p>
          ) : (
            <p className="dropdown-hint">Type at least 3 characters to search</p>
          )}

        </div>
      )}

      {/* Location Error Message */}
      {locationError && (
        <div className="address-error-message">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 5.33334V8.00001M8 10.6667H8.00667M14.6667 8.00001C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33334 11.6819 1.33334 8.00001C1.33334 4.31811 4.3181 1.33334 8 1.33334C11.6819 1.33334 14.6667 4.31811 14.6667 8.00001Z" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {locationError}
        </div>
      )}
    </div>
  );
};

export default AddressInput;
